import React from 'react';
import { useScrollReveal } from '../../hooks/useScrollReveal';
import { objectives, philosophy, procedureSteps } from '../../data/mockData';
import { useLanguage } from '../../context/LanguageContext';
import { TrendingUp, Clock, Eye, Briefcase, FileText, BookOpen, Compass, ArrowRight } from 'lucide-react';

const objectiveIcons = { TrendingUp, Clock, Eye, Briefcase };
const philosophyIcons = { FileText, BookOpen, Eye, Compass };

export default function ObjectivesSection() {
  const { t, tl } = useLanguage();
  const [sectionRef, isVisible] = useScrollReveal({ threshold: 0.1 });
  const [stepsRef, stepsVisible] = useScrollReveal({ threshold: 0.1 });

  return (
    <section className="py-16 sm:py-24 lg:py-32 bg-[#111114]">
      <div ref={sectionRef} className={`max-w-7xl mx-auto px-4 sm:px-6 lg:px-8 animate-reveal ${isVisible ? 'visible' : ''}`}>
        <div className="flex items-center gap-4 mb-6">
          <span className="text-[#c9a96e] text-xs font-semibold tracking-[0.25em] uppercase">{t('objectives_label')}</span>
          <div className="h-px flex-1 bg-[#2a2a30]" />
        </div>

        <div className="max-w-2xl mb-10 sm:mb-16">
          <h2 className="font-heading text-2xl sm:text-3xl lg:text-4xl xl:text-5xl font-bold text-[#f0ebe3] leading-tight">
            {t('objectives_h2_1')}
            <span className="text-gold-gradient block mt-1">{t('objectives_h2_2')}</span>
          </h2>
        </div>

        <div className="grid grid-cols-1 sm:grid-cols-2 lg:grid-cols-4 gap-4 sm:gap-6 mb-14 sm:mb-20">
          {objectives.map((obj, i) => {
            const Icon = objectiveIcons[obj.icon];
            return (
              <div key={i} className={`p-5 sm:p-7 border border-[#1e1e24] rounded-xl bg-[#0a0a0c]/50 service-card animate-reveal ${isVisible ? 'visible' : ''}`} style={{ transitionDelay: `${0.15 + i * 0.1}s` }}>
                <span className="text-[#c9a96e]/30 font-heading text-3xl sm:text-4xl font-bold leading-none">{obj.number}</span>
                <div className="flex items-center gap-3 mt-3 sm:mt-4 mb-2 sm:mb-3">
                  {Icon && <Icon className="w-4 h-4 sm:w-5 sm:h-5 text-[#c9a96e]" />}
                  <h3 className="text-[#f0ebe3] font-medium text-sm sm:text-[15px]">{tl(obj.title)}</h3>
                </div>
                <p className="text-[#c9a96e]/50 text-xs font-medium tracking-wider uppercase mb-2 sm:mb-3">{obj.titleRo}</p>
                <p className="text-[#8a857e] text-xs sm:text-sm leading-relaxed">{tl(obj.description)}</p>
              </div>
            );
          })}
        </div>

        <div className="mb-14 sm:mb-20">
          <h3 className="text-[#c9a96e] text-xs font-semibold tracking-[0.25em] uppercase mb-6 sm:mb-8">{t('objectives_philosophy_label')}</h3>
          <div className="grid grid-cols-1 sm:grid-cols-2 lg:grid-cols-4 gap-5 sm:gap-6">
            {philosophy.map((item, i) => {
              const Icon = philosophyIcons[item.icon];
              return (
                <div key={i} className="flex gap-3 sm:gap-4">
                  <div className="flex-shrink-0 w-9 h-9 sm:w-10 sm:h-10 rounded-lg bg-[#c9a96e]/8 flex items-center justify-center">
                    {Icon && <Icon className="w-4 h-4 text-[#c9a96e]/70" />}
                  </div>
                  <div>
                    <h4 className="text-[#f0ebe3] font-medium text-sm mb-1">{tl(item.title)}</h4>
                    <p className="text-[#6a6560] text-xs leading-relaxed">{tl(item.description)}</p>
                  </div>
                </div>
              );
            })}
          </div>
        </div>

        <div ref={stepsRef} className={`animate-reveal ${stepsVisible ? 'visible' : ''}`}>
          <h3 className="text-[#c9a96e] text-xs font-semibold tracking-[0.25em] uppercase mb-6 sm:mb-8">{t('objectives_steps_label')}</h3>
          <div className="relative">
            <div className="hidden lg:block absolute top-6 left-0 right-0 h-px bg-[#1e1e24]" />
            <div className="grid grid-cols-1 sm:grid-cols-2 lg:grid-cols-5 gap-5 sm:gap-6">
              {procedureSteps.map((step, i) => (
                <div key={i} className={`relative animate-reveal ${stepsVisible ? 'visible' : ''}`} style={{ transitionDelay: `${0.1 + i * 0.1}s` }}>
                  <div className="w-10 h-10 sm:w-12 sm:h-12 rounded-full bg-[#111114] border-2 border-[#c9a96e]/30 flex items-center justify-center mb-3 sm:mb-4 relative z-10">
                    <span className="text-[#c9a96e] font-heading text-xs sm:text-sm font-bold">{step.step}</span>
                  </div>
                  <h4 className="text-[#f0ebe3] font-medium text-sm mb-1 sm:mb-2">{tl(step.title)}</h4>
                  <p className="text-[#6a6560] text-xs leading-relaxed">{tl(step.description)}</p>
                  {i < procedureSteps.length - 1 && <ArrowRight className="hidden lg:block absolute top-3 -right-3 w-4 h-4 text-[#c9a96e]/20" />}
                </div>
              ))}
            </div>
          </div>
        </div>
      </div>
    </section>
  );
}
